import { ReflectionRoleEnum } from './reflection-role-enum'
import { ReflectionGenderEnum } from './reflection-gender-enum'
import { ReflectionStatusEnum } from './reflection-status-enum'
import { ReflectionIdcardTypeEnum } from './reflection-idcard-type-enum'
import { Guardian } from './guardian'
import { SimpleActivity } from './simple-activity'

export interface InformalReflectionInterface {
  /**
   * 关联学生 id(家长账户关联的学生 id)
   */
  pupilId: number
  /**
   * 角色
   */
  role: ReflectionRoleEnum
  /**
   * 性别
   */
  gender?: ReflectionGenderEnum | null
  /**
   * 状态
   */
  status: ReflectionStatusEnum
  /**
   * 证件类型
   */
  idcardType?: ReflectionIdcardTypeEnum | null
  /**
   * 关联家长
   */
  guardians?: Guardian[]
  /**
   * 已完成任务
   */
  completedTasks?: string
  /**
   * 进行中任务
   */
  ongoingTasks?: string
  /**
   * 用户参与的活动名
   */
  activityNames?: string
  /**
   * 用户参与的活动
   *  - 活动 name
   *  - 活动 id
   *  - 活动参与时间 joined_at
   */
  activities?: SimpleActivity[]
  /**
   * 是否已转正
   */
  isEnroll?: boolean
  name: string
  /**
   * 账户
   */
  account: string
  /**
   * 学工号
   */
  usin?: string | null
  phone?: string | null
  email?: string | null
  /**
   * 个性头像
   */
  avatar?: string | null
  /**
   * 图片
   */
  photo?: string | null
  /**
   * 证件号
   */
  idcard?: string | null
  /**
   * 届别
   */
  graduate?: string | null
  /**
   * 所属科目
   */
  subject?: string | null
  /**
   * 是否禁用
   */
  disabled: boolean
  /**
   * 是否要求修改密码
   */
  passwordChangeNeeded?: boolean
  /**
   * 注册时间
   */
  registedAt?: string | null
  /**
   * 生日
   */
  birthday?: string | null
  /**
   * 存储自定义字段的值
   */
  fields?: object
  /**
   * 主键 ID
   */
  id: number
  /**
   * 学校 ID
   */
  schoolId: number
  /**
   * 创建时间
   */
  createdAt: string
  /**
   * 更新时间
   */
  updatedAt: string
}

export class InformalReflection implements InformalReflectionInterface {
  /**
   * 关联学生 id(家长账户关联的学生 id)
   */
  pupilId: number
  /**
   * 角色
   */
  role: ReflectionRoleEnum
  /**
   * 性别
   */
  gender?: ReflectionGenderEnum | null
  /**
   * 状态
   */
  status: ReflectionStatusEnum
  /**
   * 证件类型
   */
  idcardType?: ReflectionIdcardTypeEnum | null
  /**
   * 关联家长
   */
  guardians?: Guardian[]
  /**
   * 已完成任务
   */
  completedTasks?: string
  /**
   * 进行中任务
   */
  ongoingTasks?: string
  /**
   * 用户参与的活动名
   */
  activityNames?: string
  /**
   * 用户参与的活动
   *  - 活动 name
   *  - 活动 id
   *  - 活动参与时间 joined_at
   */
  activities?: SimpleActivity[]
  /**
   * 是否已转正
   */
  isEnroll?: boolean
  name: string
  /**
   * 账户
   */
  account: string
  /**
   * 学工号
   */
  usin?: string | null
  phone?: string | null
  email?: string | null
  /**
   * 个性头像
   */
  avatar?: string | null
  /**
   * 图片
   */
  photo?: string | null
  /**
   * 证件号
   */
  idcard?: string | null
  /**
   * 届别
   */
  graduate?: string | null
  /**
   * 所属科目
   */
  subject?: string | null
  /**
   * 是否禁用
   */
  disabled: boolean
  /**
   * 是否要求修改密码
   */
  passwordChangeNeeded?: boolean
  /**
   * 注册时间
   */
  registedAt?: string | null
  /**
   * 生日
   */
  birthday?: string | null
  /**
   * 存储自定义字段的值
   */
  fields?: object
  /**
   * 主键 ID
   */
  id: number
  /**
   * 学校 ID
   */
  schoolId: number
  /**
   * 创建时间
   */
  createdAt: string
  /**
   * 更新时间
   */
  updatedAt: string

  constructor(props: InformalReflectionInterface) {
    const {
      // 重命名变量以规避与 JS 保留词的冲突, 比如 default, with
      pupilId: _pupilId,
      role: _role,
      gender: _gender,
      status: _status,
      idcardType: _idcardType,
      guardians: _guardians,
      completedTasks: _completedTasks,
      ongoingTasks: _ongoingTasks,
      activityNames: _activityNames,
      activities: _activities,
      isEnroll: _isEnroll,
      name: _name,
      account: _account,
      usin: _usin,
      phone: _phone,
      email: _email,
      avatar: _avatar,
      photo: _photo,
      idcard: _idcard,
      graduate: _graduate,
      subject: _subject,
      disabled: _disabled,
      passwordChangeNeeded: _passwordChangeNeeded,
      registedAt: _registedAt,
      birthday: _birthday,
      fields: _fields,
      id: _id,
      schoolId: _schoolId,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      // 可能会有额外字段比如自定义字段不在类型定义中
      ...rest
    } = props

    Object.assign(this, rest)

    this.pupilId = _pupilId
    this.role = _role
    this.gender = _gender
    this.status = _status
    this.idcardType = _idcardType
    this.guardians = _guardians
      ? _guardians.map((i) => new Guardian(i))
      : _guardians
    this.completedTasks = _completedTasks
    this.ongoingTasks = _ongoingTasks
    this.activityNames = _activityNames
    this.activities = _activities
      ? _activities.map((i) => new SimpleActivity(i))
      : _activities
    this.isEnroll = _isEnroll
    this.name = _name
    this.account = _account
    this.usin = _usin
    this.phone = _phone
    this.email = _email
    this.avatar = _avatar
    this.photo = _photo
    this.idcard = _idcard
    this.graduate = _graduate
    this.subject = _subject
    this.disabled = _disabled
    this.passwordChangeNeeded = _passwordChangeNeeded
    this.registedAt = _registedAt
    this.birthday = _birthday
    this.fields = _fields
    this.id = _id
    this.schoolId = _schoolId
    this.createdAt = _createdAt
    this.updatedAt = _updatedAt
  }
}
